import { SimpleEmitter } from "../core/emitter.js";
import { Plugin } from "../core/mux.js";
import { normalizeWsURL } from "../core/utils.js";
import { buildSimpleLogger } from "../core/logger.js";
import { Relay, } from "../core/relay.js";
const logPrefix = '[nostr-mux:plugin:Personalizer]';
const contactListKind = 3;
const relayListKind = 10002;
const subID = '__personalizer';
export const parseRelayListEvent = (e) => {
    if (e.kind !== relayListKind) {
        return undefined;
    }
    const entries = [];
    for (const tag of e.tags) {
        if (tag[0] !== 'r' || tag.length < 2 || tag.length > 3) {
            continue;
        }
        const url = normalizeWsURL(tag[1]);
        if (!url) {
            continue;
        }
        switch (tag.length) {
            case 2:
                entries.push({ url, read: true, write: true });
                break;
            case 3:
                entries.push({ url, read: tag[2] === 'read', write: tag[2] === 'write' });
                break;
            default:
                continue;
        }
    }
    return entries;
};
export const parseContactListEvent = (e) => {
    if (e.kind !== contactListKind) {
        return undefined;
    }
    const entries = [];
    for (const tag of e.tags) {
        if (tag[0] === 'p') {
            const entry = { pubkey: tag[1] };
            if (tag[2] && tag[2].length > 0) {
                entry.mainRelayURL = tag[2];
            }
            entries.push(entry);
        }
    }
    return entries;
};
class ReplaceableEventHolder {
    constructor(pubkey, kind) {
        this.pubkey = pubkey;
        this.kind = kind;
        this.onUpdated = new SimpleEmitter();
    }
    get leastEvent() {
        return this.least;
    }
    get targetKind() {
        return this.kind;
    }
    get initialFilter() {
        return [
            {
                kinds: [this.kind],
                authors: [this.pubkey]
            }
        ];
    }
    get recoveryFilter() {
        const filter = this.initialFilter;
        if (this.least) {
            filter[0].since = this.least.created_at;
        }
        return filter;
    }
    update(event) {
        if (this.least && (this.least.id === event.id || this.least.created_at > event.created_at)) {
            return;
        }
        if (this.accept(event)) {
            this.least = event;
            this.onUpdated.emit(event);
        }
    }
}
export class GenericReplaceableEventHolder extends ReplaceableEventHolder {
    accept() {
        return true;
    }
}
export class ContactListHolder extends ReplaceableEventHolder {
    constructor(pubkey, log) {
        super(pubkey, contactListKind);
        this.log = log;
        this.entries = [];
    }
    get currentEntries() {
        return this.entries;
    }
    accept(event) {
        const parsed = parseContactListEvent(event);
        if (!parsed) {
            return false;
        }
        this.entries = parsed;
        this.log.debug(`${logPrefix} contact list updated: ${this.entries.length} entries`);
        return true;
    }
}
export class RelayListHolder extends ReplaceableEventHolder {
    constructor(pubkey, log, mux, relayOptsTpl) {
        super(pubkey, relayListKind);
        this.log = log;
        this.mux = mux;
        this.relayOptsTpl = relayOptsTpl;
    }
    accept(event) {
        const parsed = parseRelayListEvent(event);
        if (!parsed || parsed.length === 0) {
            return false;
        }
        let added = 0, removed = 0, changed = 0;
        const alreadyAdded = new Map(this.mux.allRelays.map(r => [r.url, r]));
        for (const entry of parsed) {
            const already = alreadyAdded.get(entry.url);
            if (already) {
                changed++;
                already.updatePermission(entry);
                alreadyAdded.delete(entry.url);
            }
            else {
                added++;
                this.mux.addRelay(new Relay(entry.url, Object.assign(Object.assign({}, this.relayOptsTpl), { logger: this.log, read: entry.read, write: entry.write })));
            }
        }
        alreadyAdded.forEach((_, url) => {
            removed++;
            this.mux.removeRelay(url);
        });
        if (added > 0 || removed > 0 || changed > 0) {
            this.log.debug(`${logPrefix} relay list updated: added=${added}, removed=${removed}, changed=${changed}`);
        }
        return true;
    }
}
const atLeastOneFilter = (filters) => {
    return filters.length > 0;
};
/**
 * `Personalizer` plugin loads and applis data of user that is specified pubkey
 */
export class Personalizer extends Plugin {
    constructor(pubkey, options) {
        super();
        this.pubkey = pubkey;
        this.log = buildSimpleLogger(options.logger);
        this.flushInterval = options.flushInterval || 2000;
        this.contactListOpts = options.contactList;
        this.relayListOpts = options.relayList;
        this.genericHolders = (options.cacheReplaceableEvent || []).map(kind => (new GenericReplaceableEventHolder(this.pubkey, kind)));
        this.onUpdatedContactList = new SimpleEmitter();
        this.onUpdatedReplaceableEvent = new SimpleEmitter();
        if (this.contactListOpts.enable && this.genericHolders.find(h => h.targetKind === contactListKind)) {
            throw new Error(`${logPrefix} contactList and cacheReplaceableEvent are conflicted`);
        }
        if (this.relayListOpts.enable && this.genericHolders.find(h => h.targetKind === relayListKind)) {
            throw new Error(`${logPrefix} relayList and cacheReplaceableEvent are conflicted`);
        }
    }
    get contactListEntries() {
        var _a;
        return ((_a = this.contactList) === null || _a === void 0 ? void 0 : _a.currentEntries) || [];
    }
    getCachedReplaceableEvent(kind) {
        var _a;
        return (_a = this.genericHolders.find(h => h.targetKind === kind)) === null || _a === void 0 ? void 0 : _a.leastEvent;
    }
    id() {
        return `personalize_${this.pubkey}`;
    }
    install(mux) {
        this.mux = mux;
        const holders = [...this.genericHolders];
        for (const holder of holders) {
            holder.onUpdated.listen(event => this.onUpdatedReplaceableEvent.emit(event));
        }
        if (this.contactListOpts.enable) {
            this.contactList = new ContactListHolder(this.pubkey, this.log);
            this.contactList.onUpdated.listen(() => { var _a; return this.onUpdatedContactList.emit(((_a = this.contactList) === null || _a === void 0 ? void 0 : _a.currentEntries) || []); });
            holders.push(this.contactList);
        }
        if (this.relayListOpts.enable) {
            this.relayList = new RelayListHolder(this.pubkey, this.log, mux, this.relayListOpts.relayOptionsTemplate || {});
            holders.push(this.relayList);
        }
        const filters = holders.map(h => h.initialFilter).flat();
        if (!atLeastOneFilter(filters)) {
            this.log.warn(`${logPrefix} is disabled all functions`);
            return;
        }
        this.mux.subscribe({
            id: subID,
            filters,
            onEvent: (messages) => {
                const orderByLatest = [...messages].sort((a, b) => b.received.event.created_at - a.received.event.created_at);
                for (const holder of holders) {
                    const latest = orderByLatest.find(e => e.received.event.kind === holder.targetKind);
                    if (latest) {
                        holder.update(latest.received.event);
                    }
                }
            },
            enableBuffer: {
                flushInterval: this.flushInterval,
            },
            onRecovered: () => holders.map(h => h.recoveryFilter).flat()
        });
    }
    uninstall() {
        var _a, _b;
        (_a = this.mux) === null || _a === void 0 ? void 0 : _a.unSubscribe(subID);
        (_b = this.contactList) === null || _b === void 0 ? void 0 : _b.onUpdated.reset();
        this.contactList = undefined;
        this.relayList = undefined;
        this.genericHolders = (this.genericHolders || []).map(holder => (new GenericReplaceableEventHolder(this.pubkey, holder.targetKind)));
    }
    capturePublishedEvent(event) {
        var _a, _b, _c;
        switch (event.kind) {
            case contactListKind:
                (_a = this.contactList) === null || _a === void 0 ? void 0 : _a.update(event);
                break;
            case relayListKind:
                (_b = this.relayList) === null || _b === void 0 ? void 0 : _b.update(event);
                break;
            default:
                (_c = this.genericHolders.find(h => h.targetKind === event.kind)) === null || _c === void 0 ? void 0 : _c.update(event);
                break;
        }
    }
}
